/*
 * The timezone the Sales Task List's date filters are expressed in: IST until
 * the user explicitly picks a header timezone (Redux home.tz_user_selected),
 * then the header's (home.iana_timezone). Sent to goserver as ?tz= so a
 * YYYY-MM-DD day means that zone's local midnight-to-midnight.
 */

import moment from 'moment-timezone';
import { zoneAbbrev } from './local_time';

// goserver reads a missing or unknown ?tz= as IST (filterLocation), so the
// client falls back to the same zone to keep its defaults in step.
export const FILTER_FALLBACK_TZ = 'Asia/Kolkata';

export const resolveFilterTz = (tz?: string | null): string =>
  tz && moment.tz.zone(tz) ? tz : FILTER_FALLBACK_TZ;

// The page's zone. The header defaults to LA on load and the student profile
// sets it automatically, so neither counts until the user picks one.
export const pageFilterTz = (
  ianaTimezone: string | null | undefined,
  userSelected: boolean | null | undefined
): string => (userSelected ? resolveFilterTz(ianaTimezone) : FILTER_FALLBACK_TZ);

// The calendar day `offset` days from `now`, in tz, as YYYY-MM-DD.
export const dayInTz = (
  tz: string | null | undefined,
  offset: number,
  now: number | Date = Date.now()
): string => moment(now).tz(resolveFilterTz(tz)).add(offset, 'day').format('YYYY-MM-DD');

// Short zone label for the filter ("PST", "IST"); the IANA name when there is none.
export const filterTzAbbr = (
  tz: string | null | undefined,
  now: number | Date = Date.now()
): string => {
  const zone = resolveFilterTz(tz);
  return zoneAbbrev(zone, now) || zone;
};

// A due instant in tz, "18 Jul 2026 3:04 PM PST" (the server's IST layout plus
// the zone); '' when the instant is missing or unparseable.
export const formatDueInTz = (iso: string | null | undefined, tz: string | null | undefined) => {
  if (!iso) return '';
  const m = moment(iso, moment.ISO_8601, true);
  if (!m.isValid()) return '';
  const zone = resolveFilterTz(tz);
  return `${m.tz(zone).format('DD MMM YYYY h:mm A')} ${filterTzAbbr(zone, m.valueOf())}`;
};
